/**
 * The creature, drawn as a picture rather than loaded as one.
 *
 * It is an SVG built out of shapes right here in the code, which means every
 * part of it can be changed by editing numbers and colours below -- no image
 * editor, no files to re-export. The mood changes its eyes, its mouth and how
 * it moves; the animation itself lives in styles.css under "the creature".
 *
 * ---------------------------------------------------------------------------
 * Want to redesign it? Good. Start here.
 *
 *   - the eyes and mouth for each mood are the two lookup tables below
 *   - its colours are the CSS variables --creature-* in styles.css
 *   - how it bobs, sways and blinks is the @keyframes in styles.css
 *   - WHEN it feels each mood is lib/creature.ts, not this file
 */
import { useState } from "react";
import type { Moment, Mood } from "../lib/creature";
import type { Poop } from "../lib/poops";

/**
 * The eyes. Each mood gets a pair, drawn relative to the face so both eyes
 * stay in step. Closed eyes are a line; open ones are a circle with a
 * highlight so it looks alive.
 */
function Eyes({ mood }: { mood: Mood }) {
  const closed = mood === "asleep" || mood === "sleepy";
  const squinting = mood === "proud" || mood === "strong";

  if (closed) {
    return (
      <g stroke="var(--creature-ink)" strokeWidth="5" strokeLinecap="round" fill="none">
        <path d="M 74 104 q 11 9 22 0" />
        <path d="M 126 104 q 11 9 22 0" />
      </g>
    );
  }

  if (squinting) {
    // Happy, scrunched-up eyes -- the "^ ^" face.
    return (
      <g stroke="var(--creature-ink)" strokeWidth="6" strokeLinecap="round" fill="none">
        <path d="M 74 108 q 11 -13 22 0" />
        <path d="M 126 108 q 11 -13 22 0" />
      </g>
    );
  }

  const wide = mood === "waiting" || mood === "thirsty";
  const radius = wide ? 13 : 11;

  return (
    <g fill="var(--creature-ink)">
      <circle cx="85" cy="104" r={radius} />
      <circle cx="137" cy="104" r={radius} />
      {/* A dot of light in each eye. Small, but it is most of the charm. */}
      <circle cx="81" cy="100" r={radius / 3} fill="var(--creature-shine)" />
      <circle cx="133" cy="100" r={radius / 3} fill="var(--creature-shine)" />
    </g>
  );
}

/** The mouth. One path per mood, all drawn in the same place. */
function Mouth({ mood }: { mood: Mood }) {
  const paths: Record<Mood, string> = {
    // A small open "o", as if breathing.
    asleep: "M 103 134 q 8 10 16 0 q -8 -6 -16 0",
    waiting: "M 100 132 q 11 14 22 0",
    happy: "M 99 130 q 12 16 24 0",
    proud: "M 97 128 q 14 20 28 0",
    strong: "M 97 128 q 14 20 28 0",
    thirsty: "M 102 133 q 9 7 18 0",
    sleepy: "M 104 134 q 7 7 14 0",
    // Content, slightly wobbly line -- too full to smile properly.
    full: "M 100 134 q 6 6 11 0 q 5 -6 11 0",
  };

  return (
    <path
      d={paths[mood]}
      fill="none"
      stroke="var(--creature-ink)"
      strokeWidth="5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}

export function Creature({
  mood,
  name,
  moment,
}: {
  mood: Mood;
  name: string;
  moment?: Moment | null;
}) {
  const eating = moment?.kind === "eating";
  const sipping = moment?.kind === "sipping";
  const pleased = eating || sipping || moment?.kind === "thanks";
  const blushing = pleased || mood === "proud" || mood === "strong" || mood === "full";
  // While something is happening, it is wide awake and enjoying it, whatever
  // its mood was a second ago.
  const face: Mood = pleased ? "strong" : moment?.kind === "pooping" ? "thirsty" : mood;

  return (
    <svg
      className={`creature creature--${mood}${moment ? ` creature--${moment.kind}` : ""}`}
      viewBox="0 0 222 210"
      role="img"
      aria-label={`${name} looks ${mood}`}
    >
      {/* The sprout on its head. Sways on its own, slightly out of time with
          the body, which is what stops the whole thing looking mechanical. */}
      <g className="creature__sprout">
        <path
          d="M 111 62 q -4 -26 0 -34 q 4 8 0 34"
          fill="var(--creature-leaf)"
          stroke="var(--creature-leaf)"
          strokeWidth="7"
          strokeLinejoin="round"
        />
        <ellipse cx="88" cy="48" rx="20" ry="10" fill="var(--creature-leaf)" transform="rotate(-14 88 48)" />
        <ellipse cx="134" cy="48" rx="20" ry="10" fill="var(--creature-leaf)" transform="rotate(14 134 48)" />
      </g>

      <g className="creature__body">
        {/* The body, with a darker underside so it reads as round rather than
            flat. The shadow is a second ellipse clipped by the first. */}
        <ellipse cx="111" cy="120" rx="78" ry="68" fill="var(--creature-body)" />
        <path
          d="M 33 120 a 78 68 0 0 0 156 0 a 78 50 0 0 1 -156 0"
          fill="var(--creature-shade)"
        />

        {blushing && (
          <g fill="var(--creature-blush)" opacity="0.65">
            <ellipse cx="62" cy="126" rx="13" ry="8" />
            <ellipse cx="160" cy="126" rx="13" ry="8" />
          </g>
        )}

        <Eyes mood={face} />
        {eating ? (
          // A wide-open mouth, which chews once the food has gone in.
          <ellipse className="creature__chew" cx="111" cy="136" rx="17" ry="14" fill="var(--creature-ink)" />
        ) : sipping ? (
          // A small pursed mouth, on the rim of the cup.
          <ellipse className="creature__sip" cx="111" cy="136" rx="8" ry="7" fill="var(--creature-ink)" />
        ) : (
          <Mouth mood={face} />
        )}

        {/* Little feet, so it is standing on the ground rather than floating. */}
        <ellipse cx="86" cy="186" rx="17" ry="7" fill="var(--creature-shade)" />
        <ellipse cx="136" cy="186" rx="17" ry="7" fill="var(--creature-shade)" />
      </g>

      {/* The food, flying in from up and to the right, then a few crumbs. */}
      {eating && (
        <>
          <text className="creature__food" x="111" y="148" fontSize="38" textAnchor="middle">
            {moment.food}
          </text>
          <g className="creature__crumbs" fill="var(--creature-crumb)">
            <circle cx="88" cy="146" r="3.5" />
            <circle cx="134" cy="146" r="3" />
            <circle cx="122" cy="154" r="2.5" />
          </g>
        </>
      )}

      {/* The drink, tipped up to its mouth. */}
      {sipping && (
        <text className="creature__drink" x="111" y="148" fontSize="38" textAnchor="middle">
          {moment.food}
        </text>
      )}

      {/* Zs, but only when it is actually asleep. */}
      {mood === "asleep" && !moment && (
        <g className="creature__zzz" fill="var(--creature-leaf)" fontSize="22" fontWeight="700">
          <text x="176" y="66">z</text>
          <text x="194" y="44">z</text>
        </g>
      )}
    </svg>
  );
}

/**
 * One poop, drawn in the same hand as the creature, and with a face because it
 * is hard to mind a poop with a face. Tapping it plays the little vanishing
 * act and only then tells the screen it has gone.
 */
export function PoopPile({ poop, onPick, onPicked }: {
  poop: Poop;
  onPick: () => void;
  onPicked: () => void;
}) {
  const [leaving, setLeaving] = useState(false);

  function pickUp() {
    if (leaving) return;
    setLeaving(true);
    onPick();
    // A timer rather than waiting for the animation to end, because the
    // animation never plays if the phone is set to reduce motion.
    setTimeout(onPicked, 450);
  }

  return (
    <button
      className={`poop${leaving ? " poop--leaving" : ""}`}
      style={{ left: `${poop.x}%`, bottom: poop.y }}
      onClick={pickUp}
      aria-label="Pick up the poop"
    >
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <g className="poop__stink" fill="none" stroke="var(--poop-stink)" strokeWidth="2" strokeLinecap="round">
          <path d="M 12 12 q -3 -3 0 -6 q 3 -3 0 -6" />
          <path d="M 36 12 q -3 -3 0 -6 q 3 -3 0 -6" />
        </g>
        <path d="M 24 8 q 7 3 6 8 q -1 2 -12 1 q 1 -5 6 -9" fill="var(--poop-light)" />
        <ellipse cx="24" cy="23" rx="13" ry="7.5" fill="var(--poop-light)" />
        <ellipse cx="24" cy="35" rx="19" ry="9.5" fill="var(--poop)" />
        <circle cx="18" cy="33" r="2.4" fill="var(--creature-ink)" />
        <circle cx="30" cy="33" r="2.4" fill="var(--creature-ink)" />
        <path d="M 21 38 q 3 3 6 0" fill="none" stroke="var(--creature-ink)" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
      {leaving && <span className="poop__sparkle" aria-hidden="true">✨</span>}
    </button>
  );
}
